import BatteryIcon from "components/layout/status/BatteryIcon";
import WifiStatusBarIcon from "components/layout/status/WifiStatusBarIcon";
import MobileSignalStatusBarIcon from "components/layout/status/MobileSignalStatusBarIcon";
import TemperatureIcon from "components/layout/status/TemperatureIcon";
import { useRobotWifiStatus } from "lib/hooks/useRobotWifiStatus";
import { useRobotMobileStatus } from "lib/hooks/useRobotMobileStatus";
import { formatPingMs, useZeroHostPing } from "lib/hooks/useZeroHostPing";
import { useRobotControl } from "providers/RobotControlProvider";
import { useRobotProfileLiveness } from "providers/RobotProfileLivenessProvider";
import ARCName from "components/ARC/ARCName";

const StatusBar = ({ name = "", serialNumber, temperature, cpuPercent }) => {
    const { battery } = useRobotControl();
    const { robotLinkConnected } = useRobotProfileLiveness();
    const batteryLevel =
        typeof battery === "number" && !Number.isNaN(battery) ? battery : null;
    const wifi = useRobotWifiStatus();
    const mobile = useRobotMobileStatus();
    const ping = useZeroHostPing();

    const cpuDisplay =
        typeof cpuPercent === "number" && !Number.isNaN(cpuPercent) ? `${Math.round(cpuPercent)}%` : "—";
    const robotConnected = robotLinkConnected === true;

    return (
        <div className="inline-flex h-9 min-w-0 max-w-[min(40rem,100%)] flex-nowrap items-center gap-x-2 overflow-x-auto rounded-r5 bg-black100 px-2 text-h5 text-white sm:gap-x-4 sm:px-3">
            {/* Robot Name - Given more space and a distinct end margin */}
            <ARCName
                serialNumber={serialNumber || name}
                name={name}
                className="max-w-[8rem] shrink leading-tight sm:max-w-[11rem]"
                nameClassName="font-medium"
            />

            {/* Network Group */}
            <div className="flex items-center gap-x-1.5 shrink-0">
                <span
                    className="w-12 text-right tabular-nums text-black40" // Added w-12 and text-right
                    title="HTTP probe"
                >
                    {formatPingMs(ping)}
                </span>
                <WifiStatusBarIcon wifi={wifi} />
                <MobileSignalStatusBarIcon mobile={mobile} />
            </div>

            {/* Power & Performance Group */}
            <div className="flex items-center gap-x-3 shrink-0">
                <div className="flex items-center gap-x-1.5">
                    <BatteryIcon batteryLevel={batteryLevel} reverse={false} />
                    <div className="flex items-baseline gap-x-1">
                        <span className="text-black40">CPU:</span>
                        <span className="tabular-nums">{cpuDisplay}</span>
                    </div>
                </div>
            </div>

            {/* Temperature Group */}
            <div className="flex items-center gap-x-1 shrink-0">
                <span className="text-black40">Temp.</span>
                <TemperatureIcon temperature={temperature} reverse={false} />
            </div>

            {/* Robot link */}
            <div className="flex items-center gap-x-1.5 shrink-0">
                <span className="text-black40">Robot:</span>
                <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                        robotConnected ? "bg-green-500" : "bg-red-500"
                    }`}
                    title={robotConnected ? "Robot connected" : "Robot not connected"}
                    aria-label={robotConnected ? "Robot connected" : "Robot not connected"}
                    role="status"
                />
            </div>
        </div>
    );
};

export default StatusBar;
